/* eslint-disable func-names */
/* eslint-disable jsdoc/require-returns */
import { getErrorDefinition } from '@beemafrica/error-definitions';

const FALLBACK_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Check if a string looks like a stack trace (should not be shown to users).
 */
const looksLikeStack = function (msg) {
  if (typeof msg !== 'string' || !msg.trim()) return false;
  return (
    (/\s+at\s+/.test(msg) && /\.(js|ts|jsx|tsx):\d+/.test(msg)) ||
    /^\s*at\s+[/\\]/.test(msg)
  );
};

/**
 * Get the first line of a message (strip stack trace content).
 */
const firstLineOnly = function (msg) {
  if (msg == null || typeof msg !== 'string') return '';
  const first = msg.split(/\r?\n/)[0];
  const trimmed = first != null ? String(first).trim() : '';
  if (!trimmed) return '';
  if (looksLikeStack(trimmed)) return '';
  return trimmed;
};

/**
 * Get a safe, user-facing display message from an error.
 * Handles:
 * - API errors with standardized shape (error_code, message)
 * - Axios errors (error.response?.data)
 * - Plain Error objects
 * - UI errors (error_code starting with UI_)
 *
 * @param {Error|object} error - Error from catch block or API response
 * @returns {string} Safe message to show in toasts/alerts
 */
export const getDisplayMessage = function (error) {
  if (!error) return FALLBACK_MESSAGE;

  const data = error.response?.data || error;
  const errorCode = data.error_code || error.error_code;
  const rawMessage = data.message || error.message;

  // If we have a canonical error_code, use definition for fallback
  let fallback = FALLBACK_MESSAGE;
  if (errorCode) {
    try {
      const def = getErrorDefinition(errorCode);
      if (def && def.defaultMessage) {
        fallback = def.defaultMessage;
      }
    } catch {
      // Code not in definitions, keep generic fallback
    }
  }

  // Prefer API/error message if it's safe (no stack traces)
  const line = firstLineOnly(rawMessage);
  if (line && !looksLikeStack(line)) {
    return line;
  }

  return fallback;
};

/**
 * Get display message for a UI-originated error code (e.g. from validation, file upload).
 * Use when the UI throws or detects an error that matches a UI_* code.
 *
 * @param {string} code - Canonical code (e.g. 'UI_FILE_TOO_LARGE', 'UI_REQUIRED_FIELD_MISSING')
 * @param {string} [overrideMessage] - Optional custom message to use instead of default
 * @returns {string} User-facing message
 */
export const getUIMessage = function (code, overrideMessage) {
  if (
    overrideMessage &&
    typeof overrideMessage === 'string' &&
    !looksLikeStack(overrideMessage)
  ) {
    return overrideMessage.trim();
  }
  try {
    const def = getErrorDefinition(code);
    return def?.defaultMessage || FALLBACK_MESSAGE;
  } catch {
    return FALLBACK_MESSAGE;
  }
};
